import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Lock, TrendingUp } from 'lucide-react';
import { useFeatureConfig } from '@/contexts/FeatureConfigContext';

/**
 * ModuleGate Component
 * Protects routes by checking if module is in company's plan
 * Shows upgrade message if module not available
 * 
 * Now uses cached FeatureConfigContext instead of making API calls
 * 
 * Supports:
 * - requiredModule="HRM" - single module required
 * - requiredModules={['HRM', 'Support Coordination']} - any of these modules grants access
 */
export function ModuleGate({ children, requiredModule, requiredModules }) {
  const { modules, loading, hasModule } = useFeatureConfig();
  const navigate = useNavigate();

  // Determine module name for display
  const moduleName = requiredModules 
    ? requiredModules.join(' or ') 
    : requiredModule || '';

  // Check access
  const checkAccess = () => {
    // No module requirement = always access
    if (!requiredModule && !requiredModules) {
      return true;
    }

    // Check for array of modules (ANY of them grants access)
    if (requiredModules && Array.isArray(requiredModules)) {
      return requiredModules.some(mod => hasModule(mod));
    }

    // Check single module
    if (requiredModule) {
      // Special case: Referral module = employment Reference Check add-on (requires Recruitment)
      if (requiredModule === 'Referral') {
        return hasModule('Recruitment') && hasModule('Referral');
      }
      return hasModule(requiredModule);
    }

    return true;
  };

  const hasAccess = checkAccess();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="p-6 max-w-3xl mx-auto mt-12">
        <Card className="border-orange-200">
          <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-full">
                <Lock className="h-8 w-8 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-2xl">Module Not Available</CardTitle>
                <p className="text-gray-600 mt-1">
                  The <strong>{moduleName}</strong> module is not included in your current plan
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <Alert>
              <TrendingUp className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">Upgrade Your Plan to Access This Feature</p>
                <p className="text-sm">
                  This module provides powerful features to help grow your business. 
                  Contact your administrator or upgrade your subscription to unlock it.
                </p>
              </AlertDescription>
            </Alert>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
              <Button onClick={() => navigate('/company-settings')}>
                <TrendingUp className="h-4 w-4 mr-2" />
                View Plans & Upgrade
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return children;
}
